import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { ThreadMessage } from '../../types/threads';
import { claimFirstAppearance } from '../../lib/paper';
import { WritingScribble } from '../paper';
import { ToolPill } from './ToolPill';

/** Assistant markdown — mobile's ChatMarkdown look: Inter body, Newsreader-italic headings. */
const CHAT_MARKDOWN = [
  'font-body text-base leading-6 text-on-surface',
  '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:my-0 [&_p]:mb-3',
  '[&_h1]:font-headline [&_h1]:italic [&_h1]:font-normal [&_h1]:text-2xl [&_h1]:leading-[30px] [&_h1]:mb-1 [&_h1]:mt-2',
  '[&_h2]:font-headline [&_h2]:italic [&_h2]:font-normal [&_h2]:text-[21px] [&_h2]:leading-[27px] [&_h2]:mb-1 [&_h2]:mt-2',
  '[&_h3]:font-headline [&_h3]:italic [&_h3]:font-normal [&_h3]:text-lg [&_h3]:leading-6 [&_h3]:mb-1 [&_h3]:mt-1.5',
  '[&_strong]:font-semibold [&_em]:italic',
  '[&_a]:text-accent [&_a]:no-underline [&_a:hover]:underline',
  '[&_ul]:my-0 [&_ul]:mb-3 [&_ul]:pl-6 [&_ul]:list-disc [&_ol]:my-0 [&_ol]:mb-3 [&_ol]:pl-6 [&_ol]:list-decimal [&_li]:mb-1',
  '[&_blockquote]:my-0 [&_blockquote]:mb-3 [&_blockquote]:ml-0 [&_blockquote]:border-solid [&_blockquote]:border-0 [&_blockquote]:border-l-[3px] [&_blockquote]:border-outline-variant [&_blockquote]:pl-3 [&_blockquote]:text-on-surface-variant',
  '[&_code]:font-mono [&_code]:text-sm [&_code]:bg-surface-container [&_code]:px-[5px] [&_code]:py-px [&_code]:rounded-[5px]',
  '[&_pre]:my-0 [&_pre]:mb-3 [&_pre]:p-3 [&_pre]:bg-surface-container [&_pre]:rounded-[10px] [&_pre]:overflow-x-auto [&_pre]:text-sm',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
  '[&_table]:w-full [&_table]:mb-3 [&_table]:border-collapse [&_table]:text-[0.9375rem]',
  '[&_th]:p-1.5 [&_th]:text-left [&_th]:border [&_th]:border-solid [&_th]:border-outline-variant',
  '[&_td]:p-1.5 [&_td]:border [&_td]:border-solid [&_td]:border-outline-variant',
  '[&_hr]:my-3 [&_hr]:border-0 [&_hr]:border-t [&_hr]:border-solid [&_hr]:border-outline-variant',
].join(' ');

/**
 * One chat turn (mobile's MessageBubble). User = right-aligned ink bubble;
 * assistant = left, tool pills then markdown, with a writing pen while it's
 * still thinking. Internal /note and /library links navigate in-app.
 */
export const MessageBubble: React.FC<{ message: ThreadMessage; streaming?: boolean }> = ({ message, streaming }) => {
  const [first] = useState(() => claimFirstAppearance(`msg:${message.id}`));

  if (message.role === 'user') {
    return (
      <div
        className={`mb-4 max-w-[85%] self-end whitespace-pre-wrap rounded-2xl rounded-br-[5px] bg-primary px-4 py-2.5 font-body text-[0.9375rem] leading-[1.45rem] text-on-primary ${
          first ? 'pb-rise' : ''
        }`}
      >
        {message.content}
      </div>
    );
  }

  const hasContent = Boolean(message.content && message.content.length > 0);
  return (
    <div className={`mb-4 max-w-[92%] self-start ${first ? 'pb-rise' : ''}`}>
      {message.tool_calls.length > 0 && (
        <div className="mb-1 flex flex-wrap">
          {message.tool_calls.map((tc) => (
            <ToolPill key={tc.tool_use_id} call={tc} />
          ))}
        </div>
      )}
      {hasContent ? (
        <div className={CHAT_MARKDOWN}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[rehypeKatex]}
            components={{
              a: ({ node, href, children, ...props }) =>
                typeof href === 'string' && href.startsWith('/') ? (
                  <Link to={href} {...(props as any)}>
                    {children}
                  </Link>
                ) : (
                  <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                    {children}
                  </a>
                ),
            }}
          >
            {message.content as string}
          </ReactMarkdown>
        </div>
      ) : streaming ? (
        <Thinking />
      ) : null}
    </div>
  );
};

/** The pen that writes while the assistant works out its reply. */
export const Thinking: React.FC = () => (
  <div className="w-[150px] pt-1">
    <WritingScribble width={150} lines={1} />
    <p className="m-0 mt-1 font-body text-[0.8125rem] font-medium text-accent">Thinking…</p>
  </div>
);
